import { useCallback, useEffect, useState } from 'react';
import { useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import CharacterCountExtension from '@tiptap/extension-character-count';
import PlaceholderExtension from '@tiptap/extension-placeholder';
import { SvgIcon } from '../../Icon';
import {
  IBriefUserInfoFieldsFragment,
  ICommentInput,
  IImageEmbedFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../utils/types';
import { IStyled } from '../../../styles/types';
import { ImageEmbedUpload, ImageUploady } from '../../ImageUploady';
import useSuggestionState from './utils/useSuggestionState';
import useConversationImageEmbed from './utils/useConversationImageEmbed';
import * as ConversationInputUI from './ConversationInputUI';
import ConversationInputEmbed from './ConversationInputEmbed';
import { PrimaryButton, SecondaryButton } from '../../Button';
import {
  EmojiExtension,
  MentionExtension,
} from '../ConversationInputExtensions';
import {
  EmojiSuggestions,
  MentionSuggestions,
  SuggestionsPopover,
} from './Suggestions';
import { ICommentInputInfo } from '../types';
import { EmojiClickData } from 'emoji-picker-react';

interface IBaseProps extends IStyled {
  onSubmit: (
    commentInput: ICommentInput,
    commentInputInfo: ICommentInputInfo,
  ) => Promise<unknown>;
  participants?: IBriefUserInfoFieldsFragment[];
  placeholder?: string;
}

interface IConversationInputProps extends IBaseProps {
  isUpdate: false;
}
interface IUpdateConversationInputProps extends IBaseProps {
  isUpdate: true;
  htmlMessage: string | null;
  attachedImageEmbed: IImageEmbedFieldsFragment | null;
  onCancel: () => void;
}

type IProps = IConversationInputProps | IUpdateConversationInputProps;

const CHARACTER_LIMIT = 280;

// Matches letters (A-Z, a-z), numbers (0-9), and underscores (_) only
// Ensures at least one character
const SUGGESTION_TERM_REG_EXP = new RegExp(/^\w+$/);
const EMOJI_PRESENTATION_REG_EXP = new RegExp(/\p{Emoji_Presentation}/gu);

const ConversationInput = (props: IProps) => {
  const { onSubmit, participants, className, placeholder } = props;
  const isUpdateInput = props.isUpdate;

  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const { suggestionState, setSuggestionState, resetSuggestionState } =
    useSuggestionState();
  const areEmojiSuggestionsOpened =
    suggestionState.type === 'emoji' && suggestionState.term;
  const areMentionSuggestionOpened = suggestionState.type === 'mention';
  const areSuggestionsOpened =
    areEmojiSuggestionsOpened || areMentionSuggestionOpened;

  const {
    imageEmbedUploadProps,
    uploadedImagedEmbed,
    removeImageEmbed,
    startImageEmbedUpload,
  } = useConversationImageEmbed(
    isUpdateInput ? props.attachedImageEmbed : null,
  );

  const editor = useEditor({
    content: isUpdateInput ? props.htmlMessage : null,
    autofocus: 'end',
    editorProps: {
      handleKeyDown: (_, event) => {
        // When suggestion menu is opened - we handle some event keys there
        if (areSuggestionsOpened) {
          if (
            event.key === 'ArrowUp' ||
            event.key === 'ArrowDown' ||
            event.key === 'Enter'
          ) {
            return true;
          }

          // Close suggestions menu when certain event keys are pressed
          if (event.key === 'Escape' || event.key === ' ') {
            event.stopPropagation();
            resetSuggestionState();
            return true;
          }
        }

        // Disable Enter and Shift
        return event.key === 'Enter' || event.key === 'Shift';
      },
    },
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        strike: false,
      }),
      CharacterCountExtension.configure({
        limit: CHARACTER_LIMIT,
      }),
      MentionExtension.configure({
        suggestion: {
          render: () => ({
            onStart: (suggestionProps) => {
              setSuggestionState((draft) => {
                draft.type = 'mention';
                draft.range = suggestionProps.range;
              });
            },
            onUpdate: (suggestionProps) => {
              setSuggestionState((draft) => {
                draft.range = suggestionProps.range;
                draft.term = suggestionProps.query;
              });
            },
            onExit: () => {
              resetSuggestionState();
            },
          }),
        },
      }),
      PlaceholderExtension.configure({
        placeholder: placeholder || 'Reply',
      }),
      EmojiExtension.configure({
        suggestion: {
          render: () => ({
            onStart: (suggestionProps) => {
              setSuggestionState((draft) => {
                draft.type = 'emoji';
                draft.range = suggestionProps.range;
              });
            },
            onUpdate: (suggestionProps) => {
              setSuggestionState((draft) => {
                // Check user input and reset term if it doesn't pass regex
                if (SUGGESTION_TERM_REG_EXP.test(suggestionProps.query)) {
                  draft.range = suggestionProps.range;
                  draft.term = suggestionProps.query;
                } else {
                  draft.term = '';
                }
              });
            },
            onExit: () => {
              resetSuggestionState();
            },
          }),
        },
      }),
    ],
    onBlur: ({ event }) => {
      // If opened, hide suggestions menu onBlur.
      if (areSuggestionsOpened) {
        // Do nothing when user select option from suggestions menu.
        if (
          containerRef &&
          containerRef?.contains(event.relatedTarget as Node)
        ) {
          return;
        }
        resetSuggestionState();
      }
    },
  });

  const handleSubmit = useCallback(async () => {
    assertNotNil(editor);

    const editorHTML = editor.isEmpty ? null : editor.getHTML();

    const hasMention = editorHTML
      ? editorHTML.includes('data-type="mention"')
      : false;
    const hasEmoji = editorHTML
      ? EMOJI_PRESENTATION_REG_EXP.test(editorHTML)
      : false;

    // We allow to submit:
    // 1. Image embed only
    // 2. HTML message only
    // 3. Both Image embed and HTML message
    await onSubmit(
      {
        attachedImageEmbedId: uploadedImagedEmbed?.id ?? null,
        message: !editorHTML
          ? null
          : {
              html: editorHTML,
            },
      },
      {
        hasMention,
        hasEmoji,
      },
    );

    // Reset the editor
    if (!editor.isEmpty) {
      editor.commands.clearContent();
    }
    // Reset uploaded image
    if (uploadedImagedEmbed) {
      removeImageEmbed();
    }
  }, [editor, onSubmit, removeImageEmbed, uploadedImagedEmbed]);

  const handleSelectMention = (user: IBriefUserInfoFieldsFragment) => {
    assertNotNil(editor);
    assertNotNil(suggestionState.range);

    editor
      .chain()
      .focus()
      .deleteRange(suggestionState.range)
      .insertContent({
        type: 'mention',
        attrs: {
          id: user.id,
          label: user.name,
        },
      })
      .insertContent(' ')
      .run();

    // Once submitted - reset suggestion state
    resetSuggestionState();
  };

  const handleAddMention = () => {
    assertNotNil(editor);

    // Trigger mention extension on action click
    editor
      .chain()
      .focus()
      .insertContent(editor.isEmpty ? '@' : ' @')
      .run();
  };

  const handlePickEmoji = (emojiData: EmojiClickData) => {
    assertNotNil(editor);

    editor.chain().focus().insertEmoji({ label: emojiData.emoji }).run();
  };

  const handleSelectEmoji = (emojiUnicode: string) => {
    assertNotNil(editor);
    assertNotNil(suggestionState.range);

    editor
      .chain()
      .focus()
      .deleteRange(suggestionState.range)
      .insertEmoji({ label: emojiUnicode })
      .run();

    // Once submitted - reset suggestion state
    resetSuggestionState();
  };

  const handleKeyDown = useCallback<(event: KeyboardEvent) => void>(
    (event) => {
      if (
        (editor && editor.isEmpty && !uploadedImagedEmbed) ||
        areSuggestionsOpened
      ) {
        return;
      }

      // Submit new comment on Enter
      if (event.key === 'Enter') {
        handleSubmit();
      }
    },
    [areSuggestionsOpened, editor, handleSubmit, uploadedImagedEmbed],
  );

  // Add event listener to submit comment on Enter
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, false);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, false);
    };
  }, [handleKeyDown]);

  if (!editor) {
    return null;
  }

  const submitIsDisabled = editor.isEmpty && !uploadedImagedEmbed;

  return (
    <>
      <ConversationInputUI.Container
        ref={setContainerRef}
        className={className}
      >
        <ConversationInputUI.InputWrapper>
          <ConversationInputUI.StyledEditor editor={editor} />
          {uploadedImagedEmbed && (
            <ConversationInputEmbed
              uploadedImagedEmbed={uploadedImagedEmbed}
              onRemove={removeImageEmbed}
            />
          )}
        </ConversationInputUI.InputWrapper>
        <ConversationInputUI.Footer>
          <ConversationInputUI.FooterActions>
            <ConversationInputUI.StyledEmojiPicker onSelect={handlePickEmoji}>
              <SvgIcon
                name="smiley"
                width={20}
                height={20}
                isHoverable={true}
              />
            </ConversationInputUI.StyledEmojiPicker>
            <SvgIcon
              name="icon-at"
              width={20}
              height={20}
              isHoverable={true}
              onClick={handleAddMention}
            />
            <SvgIcon
              name="image-square"
              width={20}
              height={20}
              isHoverable={true}
              onClick={startImageEmbedUpload}
            />
          </ConversationInputUI.FooterActions>
          {isUpdateInput ? (
            <ConversationInputUI.ActionsWrapper>
              <SecondaryButton
                type="button"
                onClick={props.onCancel}
                size="small"
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={handleSubmit}
                size="small"
                disabled={submitIsDisabled}
              >
                Save
              </PrimaryButton>
            </ConversationInputUI.ActionsWrapper>
          ) : (
            <SvgIcon
              name="arrow-circle-up-fill"
              width={20}
              height={20}
              onClick={handleSubmit}
              isHoverable={true}
              isActive={true}
              isDisabled={submitIsDisabled}
            />
          )}
        </ConversationInputUI.Footer>

        {areEmojiSuggestionsOpened && (
          <SuggestionsPopover
            reference={containerRef}
            onClose={resetSuggestionState}
          >
            <EmojiSuggestions
              term={suggestionState.term}
              onSelect={handleSelectEmoji}
            />
          </SuggestionsPopover>
        )}
        {areMentionSuggestionOpened && (
          <SuggestionsPopover
            reference={containerRef}
            onClose={resetSuggestionState}
          >
            <MentionSuggestions
              term={suggestionState.term}
              onSelect={handleSelectMention}
              participants={participants}
            />
          </SuggestionsPopover>
        )}
      </ConversationInputUI.Container>
      <ImageUploady>
        <ImageEmbedUpload {...imageEmbedUploadProps} />
      </ImageUploady>
    </>
  );
};

export default ConversationInput;
